// filterView/parts/conditionRow.js — one condition as a row "field · mode · value · remove" (plan
// documents_admin-search, phase filter-varianten; builder B, later A's "more filters"). The field select is grouped by
// the schema groups. Every change goes through the store; update(condition, problem) shows a condition without
// rebuilding what is unchanged, so a text field keeps focus and caret while the user types.
//
// createConditionRow({ schema, store, id }) → { element, update, focus, destroy }
//   problem: null or a validate() reason; 'date' / 'option' are marked (an unfinished row without value is not)

import { createDisposers } from '../../core/disposers.js';
import { updateCondition, removeCondition } from '../conditionSet.js';
import { createValueInput } from './valueInput.js';

const PROBLEM_TEXTS = { date: 'FILTER_VIEW_PROBLEM_DATE', option: 'FILTER_VIEW_PROBLEM_OPTION', id: 'FILTER_VIEW_PROBLEM_ID' };

function fieldSelect(schema) {
    const select = document.createElement('select');
    select.className = 'topo2-field--bordered topo2-filterview-select';
    for (const group of schema.groups) {
        const fields = schema.fields.filter((f) => f.group === group.key);
        if (fields.length) {
            const optgroup = document.createElement('optgroup');
            optgroup.label = group.label;
            for (const field of fields) {
                optgroup.append(new Option(field.label, field.key));
            }
            select.append(optgroup);
        }
    }
    return select;
}

export function createConditionRow({ schema, store, id }) {
    const disposers = createDisposers();
    const texts = schema.texts;
    const change = (patch) => store.set((set) => updateCondition(set, schema, id, patch));
    const listen = (target, type, handler) => {
        target.addEventListener(type, handler);
        disposers.add(() => target.removeEventListener(type, handler));
    };

    const element = document.createElement('div');
    element.className = 'topo2-filterview-row';

    const fieldEl = fieldSelect(schema);
    fieldEl.setAttribute('aria-label', texts.FILTER_VIEW_FIELD);
    listen(fieldEl, 'change', () => change({ field: fieldEl.value }));

    const modeEl = document.createElement('select');
    modeEl.className = 'topo2-field--bordered topo2-filterview-select';
    modeEl.setAttribute('aria-label', texts.FILTER_VIEW_MODE);
    listen(modeEl, 'change', () => change({ mode: modeEl.value }));

    const valueCell = document.createElement('div');
    valueCell.className = 'topo2-filterview-value';

    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'topo2-btn topo2-btn--noborder topo2-filterview-remove';
    remove.setAttribute('aria-label', texts.FILTER_VIEW_REMOVE);
    remove.title = texts.FILTER_VIEW_REMOVE;
    const icon = document.createElement('span');
    icon.className = 'topo2-garbage';
    icon.setAttribute('aria-hidden', 'true');
    remove.append(icon);
    listen(remove, 'click', () => store.set((set) => removeCondition(set, id)));

    element.append(fieldEl, modeEl, valueCell, remove);

    let shown = { field: null, needsValue: null };
    let valueInput = null;
    disposers.add(() => valueInput?.destroy());

    return {
        element,
        update(condition, problem = null) {
            const field = schema.fields.find((f) => f.key === condition.field);
            const mode = field.modes.find((m) => m.key === condition.mode);
            if (shown.field !== field.key) {
                fieldEl.value = field.key;
                modeEl.replaceChildren(...field.modes.map((m) => new Option(m.label, m.key)));
            }
            modeEl.value = mode.key;
            if (shown.field !== field.key || shown.needsValue !== mode.value) {
                valueInput?.destroy();
                valueInput = createValueInput({
                    field, needsValue: mode.value, value: condition.value ?? '',
                    onInput: (value) => change({ value }), label: texts.FILTER_VIEW_VALUE,
                });
                valueCell.replaceChildren(valueInput.element);
                shown = { field: field.key, needsValue: mode.value };
            } else {
                valueInput.setValue(condition.value ?? '');
            }

            const message = PROBLEM_TEXTS[problem] ? texts[PROBLEM_TEXTS[problem]] : '';
            element.classList.toggle('is-invalid', message !== '');
            valueCell.title = message;
            if (mode.value && message) {
                valueInput.element.setAttribute('aria-invalid', 'true');
            } else {
                valueInput.element.removeAttribute('aria-invalid');
            }
        },
        focus() {
            fieldEl.focus();
        },
        destroy() {
            disposers.dispose();
        },
    };
}
